import { breakpoints } from 'config/breakpoints';
import { colorsCssVariables } from '../colors';
import { gridCssResponsiveVariables } from '../grid';
import { opacityCssVariables } from '../opacity';
import { ratiosCssResponsiveVariables } from '../ratios';
import { spacingCssResponsiveVariables } from '../spacing';
import { transitionsCssResponsiveVariables } from '../transitions';
import { zIndexCssResponsiveVariables } from '../z-index';
import { type Theme } from './index';

// All statics here must use the --light-- prefix so they are picked up in core-web App/Styles
// and exposed on :root for the data-theme switch. Unprefixed vars would be ignored.
const statics = {
    // Default Configs
    ...colorsCssVariables,
    ...opacityCssVariables,

    // UI
    '--light--ui-primary': 'var(--color-base-charcoal)',
    '--light--ui-secondary': 'var(--color-base-brickwork-orange)',
    '--light--ui-background': 'var(--color-base-off-white)',
    '--light--overlay-overlay-background': 'var(--color-opacity-light-beige-90)',
    '--light--stroke-default': 'var(--color-opacity-charcoal-15)',
    '--light--stroke-hover': 'var(--color-shade-medium-orange)',
    '--light--stroke-active': 'var(--color-shade-medium-orange)',
    '--light--gradient-30': 'var(--color-opacity-off-white-30)',
    '--light--gradient-0': 'var(--color-opacity-off-white-0)',
    '--light--semantic-error': 'var(--color-semantic-red)',
    '--light--semantic-warning': 'var(--color-semantic-yellow)',
    '--light--semantic-success': 'var(--color-semantic-green)',
    '--light--gradient-100': 'var(--color-base-off-white)',
    '--light--opacity-primary-50': 'var(--color-opacity-charcoal-50)',
    '--light--opacity-primary-30': 'var(--color-opacity-charcoal-30)',
    '--light--opacity-primary-15': 'var(--color-opacity-charcoal-15)',
    '--light--stroke-heavy': 'var(--color-opacity-charcoal-30)',
    '--light--stroke-full': 'var(--color-base-charcoal)',
    '--light--ui-secondary-aa': 'var(--color-shade-medium-orange)',
    '--light--ui-surface': 'var(--color-shade-light-beige)',
    '--light--ui-primary-aa': 'var(--color-base-charcoal)',

    // Button
    '--light--button-primary-background': 'var(--color-base-brickwork-orange)',
    '--light--button-secondary-background': 'var(--color-base-charcoal)',
    '--light--button-primary-text': 'var(--color-base-charcoal)',
    '--light--button-secondary-text': 'var(--color-base-off-white)',
    '--light--button-tertiery-text': 'var(--color-base-charcoal)',
    '--light--button-tertiery-stroke': 'var(--color-opacity-charcoal-15)',
    '--light--button-primary-text-hover': 'var(--color-base-off-white)',
    '--light--button-primary-text-active': 'var(--color-base-off-white)',
    '--light--button-primary-text-disabled': 'var(--color-opacity-charcoal-50)',
    '--light--button-primary-background-disabled': 'var(--color-shade-medium-orange)',
    '--light--button-secondary-text-hover': 'var(--color-shade-medium-orange)',
    '--light--button-secondary-text-active': 'var(--color-shade-medium-orange)',
    '--light--button-secondary-text-disabled': 'var(--color-opacity-off-white-50)',
    '--light--button-secondary-background-disabled': 'var(--color-shade-light-charcoal)',
    '--light--button-tertiery-text-hover': 'var(--color-shade-medium-orange)',
    '--light--button-tertiery-text-active': 'var(--color-shade-medium-orange)',
    '--light--button-tertiery-text-disabled': 'var(--color-opacity-charcoal-50)',
    '--light--button-tertiery-stroke-hover': 'var(--color-opacity-orange-15)',
    '--light--button-tertiery-stroke-active': 'var(--color-opacity-orange-15)',
    '--light--button-tertiery-stroke-heavy': 'var(--color-opacity-charcoal-30)',
    '--light--button-tertiery-text-focus-outline': '2px solid var(--light--button-tertiery-text)',
};

const responsives = {} as Theme['responsives'];
breakpoints.forEach((breakpoint) => {
    responsives[breakpoint.label] = {
        ...ratiosCssResponsiveVariables[breakpoint.label],
        ...gridCssResponsiveVariables[breakpoint.label],
        ...transitionsCssResponsiveVariables[breakpoint.label],
        ...zIndexCssResponsiveVariables[breakpoint.label],
        ...spacingCssResponsiveVariables[breakpoint.label],

        ...responsives[breakpoint.label],
    };
});

const theme: Theme = {
    statics,
    responsives,
};

export default theme;
